import { Feature, FeatureCollection, Geometry } from 'geojson';
import { useMemo } from 'react';
import { Source } from 'react-map-gl';

import { PhotoMarkerIcon, ReportIconFov } from 'features/mapping/icons';
import { ReportObfuscationLayer } from 'features/mapping/components/layers/ReportObfuscationLayer';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import { MapboxFeature } from 'features/mapping/mapping.types';
import { Report } from 'features/reports/report.types';
import { isRingReport } from 'features/reports/reports.utils';
import { history } from 'shared/history';
import { Layer, LayerProps } from 'shared/map-exports';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';

export type PhotoMarkersLayerProps = {
  reports: Report[];
  hoveredReportId: number | null;
};

export type PhotoMarkerFeatureProperties = {
  id: number;
  azimuth: number;
};

const PHOTO_MARKERS_SOURCE_ID = 'photo-markers';
export const PHOTO_MARKERS_LAYER_ID = 'photo-markers-icons';
const PHOTO_MARKERS_HIGHLIGHT_LAYER_ID = 'photo-markers-highlight';
const PHOTO_MARKERS_VIEWSHED_LAYER_ID = 'photo-markers-viewsheds';
const PHOTO_MARKERS_OBFUSCATION_LAYER_ID = 'photo-markers-obfuscation';

const PHOTO_MARKERS_VIEWSHED_MINZOOM = 8;

const iconStyle: LayerProps = {
  type: 'symbol',
  layout: {
    'icon-size': 1,
    'icon-image': PhotoMarkerIcon.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false,
  },
};

const viewshedStyle: LayerProps = {
  type: 'symbol',
  minzoom: PHOTO_MARKERS_VIEWSHED_MINZOOM,
  layout: {
    'icon-size': 1,
    'icon-image': ReportIconFov.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false,
    'icon-rotate': ['get', 'azimuth'],
    'icon-offset': ReportIconFov.offset,
    'icon-rotation-alignment': 'map',
  },
  filter: ['>', 'azimuth', 0],
};

const getHighlightStyle = (hoveredReportId: number | null): LayerProps => ({
  type: 'circle',
  filter: ['==', ['get', 'id'], hoveredReportId ?? -1],
  paint: {
    'circle-radius': 34,
    'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
    'circle-blur': 1,
  },
});

const toGeojsonFeature = (
  report: Report,
): Feature<Geometry, PhotoMarkerFeatureProperties> | null => {
  const media = report.media?.[0];
  const { lat, lng } = media ?? {};
  if (lat == null || lng == null) {
    return null;
  }

  const azimuth = media?.az || 0;

  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: { id: report.id, azimuth },
  };
};

export const photoMarkerClickHandler = (
  geoJsonFeatures: MapboxFeature<PhotoMarkerFeatureProperties>[],
): void => {
  const feature = geoJsonFeatures[0];
  if (!feature) return;

  const { id } = feature.properties;
  history.push(`/reports/submission/${id}`, {
    returnOnSuccess: true,
  });
};

export const PhotoMarkersLayer = (
  props: PhotoMarkersLayerProps,
): JSX.Element | null => {
  const { reports, hoveredReportId } = props;

  const geojson: FeatureCollection<Geometry, PhotoMarkerFeatureProperties> =
    useMemo(
      () => ({
        type: 'FeatureCollection',
        features: reports
          .map((report) => toGeojsonFeature(report))
          .filter(
            (
              feature,
            ): feature is Feature<Geometry, PhotoMarkerFeatureProperties> =>
              feature !== null,
          ),
      }),
      [reports],
    );

  const highlightStyle = useMemo(
    () => getHighlightStyle(hoveredReportId),
    [hoveredReportId],
  );

  const obfuscatedReports = useMemo(
    () => reports.filter((report: Report) => isRingReport(report)),
    [reports],
  );

  return (
    <Source id={PHOTO_MARKERS_SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={PHOTO_MARKERS_HIGHLIGHT_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...highlightStyle}
      />
      <Layer
        id={PHOTO_MARKERS_VIEWSHED_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...viewshedStyle}
      />
      <Layer
        id={PHOTO_MARKERS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...iconStyle}
        metadata={{ interactive: true }}
      />
      {obfuscatedReports.length > 0 && (
        <ReportObfuscationLayer
          beforeId={PHOTO_MARKERS_HIGHLIGHT_LAYER_ID}
          reports={obfuscatedReports}
          sourceId={PHOTO_MARKERS_OBFUSCATION_LAYER_ID}
        />
      )}
    </Source>
  );
};
